import { useMutation, useQuery } from '@apollo/client'
import { COUNTRY_LIST, PROVINCES_LIST } from 'constants'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useOutletContext, useParams } from 'react-router-dom'

import {
  Button,
  CardBox,
  Fields,
  FormControl,
  InputLabel,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  useAlert,
} from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'

export function RepresentativeDetailsForm() {
  const {
    setHasUnsavedChanges: trackUnsavedChanges,
    profileFormRef,
    onSubmitSuccess: onSuccess,
    onSubmitFail: onFail,
  } = useOutletContext()

  const { id: charityId } = useParams()
  const { data: { me } = {} } = useQuery(queries.user.me)
  const fundProfile = me?.funds?.find((f) => f.name === `charity-${charityId}`)?.fundProfile
  const address = fundProfile?.address
  const charityUser = me?.charityUsers?.find((cu) => String(cu.charityId) === String(charityId))

  const { register, handleSubmit, formState, setError, getValues, setValue, watch, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      title: charityUser?.title ?? '',
      fundPhone: fundProfile?.phone ?? '',
      address: {
        lineOne: address?.lineOne ?? '',
        lineTwo: address?.lineTwo ?? '',
        city: address?.city ?? '',
        province: address?.province ?? '',
        postalCode: address?.postalCode ?? '',
        country: address?.country ?? 'Canada',
      },
    },
  })
  const { isDirty, isSubmitting, errors = {} } = formState
  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const countryValue = watch('address.country')
  const provinceValue = watch('address.province')

  const effectiveCountry = countryValue ?? address?.country ?? 'Canada'
  const effectiveProvince = provinceValue ?? address?.province ?? ''

  useEffect(() => {
    trackUnsavedChanges(isDirty)
  }, [countryValue, isDirty, trackUnsavedChanges])

  const countryIsCanada = effectiveCountry === 'Canada' || effectiveCountry === 'CAN' || effectiveCountry === 'CA'

  const [updateProfile] = useMutation(mutations.user.updateProfile, {
    onCompleted: () => {
      setAlert({ message: 'Representative details updated', severity: 'success' })
      reset(getValues())
      onSuccess?.()
    },
    onError: (serverErrors) => {
      parseAndSetFormErrors(serverErrors, setError)
      setAlert({ message: 'Update failed', severity: 'error' })
      onFail?.()
    },
    refetchQueries: [{ query: queries.user.me }],
  })

  const onSubmit = async (data) => {
    updateProfile({
      variables: {
        data: {
          address: data.address,
          fundPhone: data.fundPhone,
          title: data.title,
          charityId,
        },
      },
    })
  }

  const onInvalid = () => {
    onFail?.()
  }

  return (
    <CardBox>
      <Text.H4>Representative Details</Text.H4>
      <br />
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} ref={profileFormRef}>
        <Stack>
          <Text.H6>Job Title</Text.H6>
          <Fields.Text
            inputProps={{ 'data-testid': 'title' }}
            label="Job Title / Role"
            error={!!errors.title}
            helperText={errors?.title?.message}
            defaultValue={charityUser?.title ?? ''}
            {...register('title')}
          />

          <Text.H6 sx={{ mt: 2 }}>Mailing Address</Text.H6>
          <Fields.Text
            inputProps={{ 'data-testid': 'addrLineOne' }}
            type="text"
            label="Address Line One"
            error={!!errors.address?.lineOne}
            helperText={errors?.address?.lineOne?.message}
            defaultValue={address?.lineOne}
            {...register('address.lineOne')}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'addrLineTwo' }}
            type="text"
            label="Address Line Two"
            error={!!errors.address?.lineTwo}
            helperText={errors?.address?.lineTwo?.message}
            defaultValue={address?.lineTwo}
            {...register('address.lineTwo')}
          />
          <Fields.Text
            inputProps={{ 'data-testid': 'city' }}
            type="text"
            label="City"
            error={!!errors.address?.city}
            helperText={errors?.address?.city?.message}
            defaultValue={address?.city}
            {...register('address.city')}
          />

          {countryIsCanada ? (
            <FormControl fullWidth>
              <InputLabel id="provinceLabel">Province</InputLabel>
              <Select
                inputProps={{ 'data-testid': 'province' }}
                value={effectiveProvince}
                onChange={(e) => setValue('address.province', e.target.value, { shouldValidate: true })}
                label="Province"
                labelId="provinceLabel"
                aria-label="Province"
                error={!!errors.address?.province}
                helperText={errors?.address?.province?.message}
                defaultValue={address?.province}
                {...register('address.province')}
              >
                {PROVINCES_LIST.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Fields.Text
              inputProps={{ 'data-testid': 'province' }}
              type="text"
              label="Province / State"
              error={!!errors.address?.province}
              helperText={errors?.address?.province?.message}
              defaultValue={address?.province}
              {...register('address.province')}
            />
          )}

          {countryIsCanada ? (
            <Fields.Text
              inputProps={{ 'data-testid': 'postalCode' }}
              type="text"
              label="Postal Code"
              error={!!errors.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              defaultValue={address?.postalCode}
              {...register('address.postalCode', { ...validations.postalCode })}
            />
          ) : (
            <Fields.Text
              inputProps={{ 'data-testid': 'postalCode' }}
              type="text"
              label="Postal Code / Zip Code"
              error={!!errors.address?.postalCode}
              helperText={errors?.address?.postalCode?.message}
              defaultValue={address?.postalCode}
              {...register('address.postalCode')}
            />
          )}

          <FormControl fullWidth>
            <InputLabel id="countryLabel">Country</InputLabel>
            <Select
              inputProps={{ 'data-testid': 'country' }}
              value={effectiveCountry}
              onChange={(e) => setValue('address.country', e.target.value, { shouldValidate: true })}
              label="Country"
              labelId="countryLabel"
              aria-label="Country"
              error={!!errors.address?.country}
              helperText={errors?.address?.country?.message}
              defaultValue={address?.country ?? 'Canada'}
              {...register('address.country')}
            >
              {COUNTRY_LIST.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Text.H6 sx={{ mt: 2 }}>Phone Number</Text.H6>
          <Fields.Text
            inputProps={{ 'data-testid': 'fundPhone' }}
            type="text"
            label="Phone"
            error={!!errors.fundPhone}
            helperText={errors?.fundPhone?.message}
            defaultValue={fundProfile?.phone}
            {...register('fundPhone', { ...validations.phoneNumber })}
          />

          <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
            <Button type="submit" disabled={isSubmitting} data-testid="updateRepresentativeDetailsButton">
              Update Details
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
    </CardBox>
  )
}
